import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";

const API_URL = process.env.REACT_APP_BASE_URL;

/**
 * 📄 useEmployeeDocuments Hook
 * يدير نظام المستندات والشهادات للموظفين
 */
export function useEmployeeDocuments() {
  // ============== MODAL STATES ==============
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);

  // ============== SELECTED EMPLOYEE & DOCUMENTS ==============
  const [selectedEmployeeDocuments, setSelectedEmployeeDocuments] = useState(null);

  // ============== LOADING & SAVING STATES ==============
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [savingDocument, setSavingDocument] = useState(false);

  // ============== UNSAVED CHANGES TRACKING ==============
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // ============== DOCUMENT CHECKLIST STATE ==============
  const [documentChecklist, setDocumentChecklist] = useState({
    photosCount: false,
    birthCertificateCopy: false,
    gradCertificateOriginal: false,
    idCopy: false,
    noCrimeCertificate: false,
    laborLetterOriginal: false,
    medicalCheckForm111: false,
  });

  // ============== HELPER FUNCTION: RESET DOCUMENTS STATE ==============
  const resetDocumentsState = () => {
    setShowDocumentsModal(false);
    setSelectedEmployeeDocuments(null);
    setLoadingDocuments(false);
    setSavingDocument(false);
    setHasUnsavedChanges(false);
    setDocumentChecklist({
      photosCount: false,
      birthCertificateCopy: false,
      gradCertificateOriginal: false,
      idCopy: false,
      noCrimeCertificate: false,
      laborLetterOriginal: false,
      medicalCheckForm111: false,
    });
  };

  // ============== HELPER FUNCTION: LOAD EMPLOYEE DOCUMENTS (with real API fetch) ==============
  const loadEmployeeDocuments = async (employee) => {
    try {
      setLoadingDocuments(true);
      setSelectedEmployeeDocuments(employee);
      setShowDocumentsModal(true);
      setHasUnsavedChanges(false);

      const empData = employee.fullEmployeeData || {};
      setDocumentChecklist({
        photosCount: empData.photosCount === 1 || empData.photosCount === true,
        birthCertificateCopy: empData.birthCertificateCopy || false,
        gradCertificateOriginal: empData.gradCertificateOriginal || false,
        idCopy: empData.idCopy || false,
        noCrimeCertificate: empData.noCrimeCertificate || false,
        laborLetterOriginal: empData.laborLetterOriginal || false,
        medicalCheckForm111: empData.medicalCheckForm111 || false,
      });

      // Fetch real uploaded documents from API (use infinityCode, not id)
      const infinityCode = employee.infinityCode || employee.fullEmployeeData?.infinityCode || employee.id;
      const response = await apiFetch(
        `${API_URL}/api/documents/${infinityCode}`,
        { method: 'GET' }
      );
      const responseData = await response.json();

      setSelectedEmployeeDocuments({
        ...employee,
        documents: responseData || [],
      });
    } catch (err) {
      console.error("❌ Error loading employee documents:", err);
      setSelectedEmployeeDocuments((prev) => prev ? { ...prev, documents: [] } : prev);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // ============== HELPER FUNCTION: UPDATE DOCUMENT CHECKBOX ==============
  const updateDocumentCheckbox = (documentField, value) => {
    setDocumentChecklist((prev) => ({
      ...prev,
      [documentField]: value,
    }));
    setHasUnsavedChanges(true);
  };

  // ============== HELPER FUNCTION: BUILD DOCUMENTS PAYLOAD ==============
  const buildDocumentsPayload = () => ({
    photosCount: documentChecklist.photosCount ? true : false,
    birthCertificateCopy: documentChecklist.birthCertificateCopy,
    gradCertificateOriginal: documentChecklist.gradCertificateOriginal,
    idCopy: documentChecklist.idCopy,
    noCrimeCertificate: documentChecklist.noCrimeCertificate,
    laborLetterOriginal: documentChecklist.laborLetterOriginal,
    medicalCheckForm111: documentChecklist.medicalCheckForm111,
  });

  // ============== SAVE DOCUMENTS (PUT like EmployeeTable.js) ==============
  const saveDocuments = async (employeeId, setEmployees) => {
    try {
      if (!employeeId) return false;
      setSavingDocument(true);

      const payload = buildDocumentsPayload();

      const response = await apiFetch(
        `${API_URL}/employees/${employeeId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(responseData?.message || responseData?.error || `Server error: ${response.status}`);
      }

      // Update local employees list if setter provided
      if (typeof setEmployees === 'function') {
        setEmployees((prev) =>
          prev.map((emp) =>
            emp.id === employeeId
              ? { ...emp, fullEmployeeData: { ...(emp.fullEmployeeData || {}), ...payload } }
              : emp
          )
        );
      }

      // Update selectedEmployeeDocuments locally
      setSelectedEmployeeDocuments((prev) =>
        prev ? { ...prev, fullEmployeeData: { ...(prev.fullEmployeeData || {}), ...payload } } : prev
      );

      setHasUnsavedChanges(false);
      return true;
    } catch (err) {
      console.error('❌ Failed to save documents:', err);
      throw err;
    } finally {
      setSavingDocument(false);
    }
  };

  // ============== RETURN PUBLIC API ==============
  return {
    // Modal state
    showDocumentsModal,
    setShowDocumentsModal,

    // Employee & documents
    selectedEmployeeDocuments,
    setSelectedEmployeeDocuments,

    // Loading states
    loadingDocuments,
    setLoadingDocuments,
    savingDocument,
    setSavingDocument,

    // Unsaved changes
    hasUnsavedChanges,
    setHasUnsavedChanges,

    // Document checklist
    documentChecklist,
    setDocumentChecklist,

    // Helper methods
    resetDocumentsState,
    loadEmployeeDocuments,
    updateDocumentCheckbox,
    buildDocumentsPayload,
    saveDocuments,
  };
}
